import { Content } from "antd/es/layout/layout";
import React from "react";
import { Input, Radio, Form, Button } from "antd";
import { LeftOutlined } from "@ant-design/icons";
import "./style.css";
import { useLocation, useNavigate } from "react-router-dom";
import { generateClient } from "aws-amplify/api";
import * as mutations from "../graphql/mutations";
import { getTableID } from "../Services/authServices";

const formatBarbadosPhone = (rawValue) => {
  const digits = (rawValue || "").replace(/\D/g, "");
  const limited = digits.slice(0, 7);
  if (limited.length <= 3) return limited;
  return `${limited.slice(0, 3)}-${limited.slice(3)}`;
};

const AddRecipient = () => {
  const [form] = Form.useForm();
  const selectedRecipientType = Form.useWatch("typeofRecipient", form);
  const selectedDeliveryMethod = Form.useWatch("recipientDeliveryMethod", form);

  const navigate = useNavigate();
  const { state } = useLocation();
  const API = generateClient();
  const isSendMoneyFlow = state?.flow === "sendMoney";
  const sendDraft = state?.sendDraft;
  const preselectedDeliveryMethod = state?.recipientDeliveryMethod;

  const handlePhoneChange = (event) => {
    const formatted = formatBarbadosPhone(event?.target?.value || "");
    form.setFieldsValue({ phoneNumber: formatted });
  };

  const onFinish = async (values) => {
    const tableID = await getTableID();
    if (!tableID) {
      console.error("Table ID is not available.");
      return;
    }

    const cleanedPhoneNumber = values.phoneNumber.replace(/\D/g, "");
    const fullPhoneNumber = `+1246${cleanedPhoneNumber}`;

    const addressStreet = values.addressLine2
      ? `${values.addressLine1}, ${values.addressLine2}`
      : values.addressLine1;

    const input = {
      firstName: values.firstName,
      lastName: values.lastName,
      businessName: values.businessName || "",
      phoneNumber: fullPhoneNumber,
      email: values.email,
      recipientDeliveryMethod: values.recipientDeliveryMethod,
      addressState: values.parish,
      addressCity: values.cityTown,
      addressStreet,
      userID: tableID,
      addressCountry: "Barbados",
      addressZipCode: "",
      typeOfRecipient: values.typeofRecipient,
      isRecipientVerified: false,
      bankName: values.bankName || "",
      bankBranch: values.bankBranch || "",
      bankAccountHolderName: values.bankAccountHolderName || "",
      bankAccountNumber: values.bankAccountNumber || "",
    };

    try {
      await API.graphql({
        query: mutations.createRecipient,
        variables: { input },
      });

      navigate("/recipients", {
        state: isSendMoneyFlow ? { flow: "sendMoney", sendDraft } : undefined,
        replace: true,
      });
    } catch (err) {
      console.error("Error creating recipient:", err);
    }
  };

  return (
    <Content className="appPageContent addRecipientPage">
      <div className="recHeadingDiv appPageShell appPageShellNarrow addRecipientWrapper">
        <div className="addRecipientHeader appSectionHeader appPageHeader">
          <Button
            type="text"
            icon={<LeftOutlined />}
            onClick={() =>
              navigate("/recipients", {
                state: isSendMoneyFlow ? { flow: "sendMoney", sendDraft } : undefined,
                replace: true,
              })
            }
            className="addRecipientBackBtn"
          />
          <div className="addRecipientHeaderMain">
            <h2 className="appSectionTitle">New Recipient</h2>
          </div>
        </div>

        <div className="recMainDiv addRecipientCard">
          <Form
            layout="vertical"
            onFinish={onFinish}
            form={form}
            className="addRecipientForm"
            initialValues={{
              typeofRecipient: "Personal",
              recipientCountry: "Barbados",
              recipientDeliveryMethod: preselectedDeliveryMethod || "Bank Transfer",
            }}
          >
            <div className="addRecipientSection">
              <h3 className="addRecipientSectionTitle">Recipient Type</h3>
              <Form.Item
                name="typeofRecipient"
                label=""
                rules={[{ required: true, message: "Please select recipient type" }]}
              >
                <Radio.Group className="recipientTypeGroup" buttonStyle="solid">
                  <Radio.Button value="Personal">Personal</Radio.Button>
                  <Radio.Button value="Business">Business</Radio.Button>
                </Radio.Group>
              </Form.Item>

              {selectedRecipientType === "Business" && (
                <Form.Item
                  name="businessName"
                  label="Business Name"
                  rules={[{ required: true, message: "Please input business name" }]}
                >
                  <Input size="large" />
                </Form.Item>
              )}

              <div className="addRecipientRow">
                <Form.Item
                  className="addRecipientHalfField"
                  name="firstName"
                  label="First Name"
                  rules={[{ required: true, message: "Please input first name" }]}
                >
                  <Input size="large" />
                </Form.Item>
                <Form.Item
                  className="addRecipientHalfField"
                  name="lastName"
                  label="Last Name"
                  rules={[{ required: true, message: "Please input last name" }]}
                >
                  <Input size="large" />
                </Form.Item>
              </div>
            </div>

            <div className="addRecipientSection">
              <h3 className="addRecipientSectionTitle">Contact Information</h3>
              <div className="addRecipientRow">
                <Form.Item
                  className="addRecipientHalfField"
                  name="phoneNumber"
                  label="Phone Number"
                  rules={[
                    { required: true, message: "Please enter the phone number" },
                    () => ({
                      validator(_, value) {
                        const digits = (value || "").replace(/\D/g, "");
                        if (digits.length === 7) return Promise.resolve();
                        return Promise.reject(new Error("Please enter a valid phone number."));
                      },
                    }),
                  ]}
                >
                  <Input
                    size="large"
                    maxLength={8}
                    addonBefore="+1246"
                    placeholder="XXX-XXXX"
                    onChange={handlePhoneChange}
                  />
                </Form.Item>

                <Form.Item
                  className="addRecipientHalfField"
                  name="email"
                  label="Email"
                  rules={[
                    { required: true, message: "Please input E-mail" },
                    { type: "email", message: "The input is not valid E-mail" },
                  ]}
                >
                  <Input size="large" />
                </Form.Item>
              </div>
            </div>

            <div className="addRecipientSection">
              <h3 className="addRecipientSectionTitle">Address</h3>
              <Form.Item
                name="addressLine1"
                label="Address Line 1"
                rules={[{ required: true, message: "Please input address line 1" }]}
              >
                <Input size="large" />
              </Form.Item>

              <Form.Item name="addressLine2" label="Address Line 2">
                <Input size="large" />
              </Form.Item>

              <div className="addRecipientRow">
                <Form.Item
                  className="addRecipientHalfField"
                  name="cityTown"
                  label="City / Town"
                  rules={[{ required: true, message: "Please input city/town" }]}
                >
                  <Input size="large" />
                </Form.Item>
                <Form.Item
                  className="addRecipientHalfField"
                  name="parish"
                  label="Parish"
                  rules={[{ required: true, message: "Please input parish" }]}
                >
                  <Input size="large" />
                </Form.Item>
              </div>

              <Form.Item label="Country">
                <Input size="large" value="Barbados" disabled />
              </Form.Item>
            </div>

            {selectedDeliveryMethod === "Bank Transfer" ? (
              <div className="addRecipientSection">
                <h3 className="addRecipientSectionTitle">Bank Details</h3>

                <div className="addRecipientRow">
                  <Form.Item
                    className="addRecipientHalfField"
                    name="bankName"
                    label="Bank Name"
                    rules={[{ required: true, message: "Please enter bank name" }]}
                  >
                    <Input size="large" />
                  </Form.Item>

                  <Form.Item
                    className="addRecipientHalfField"
                    name="bankBranch"
                    label="Branch"
                    rules={[{ required: true, message: "Please enter branch" }]}
                  >
                    <Input size="large" />
                  </Form.Item>
                </div>

                <div className="addRecipientRow">
                  <Form.Item
                    className="addRecipientHalfField"
                    name="bankAccountHolderName"
                    label="Account Holder Name"
                    rules={[
                      {
                        required: true,
                        message: "Please enter account holder name",
                      },
                    ]}
                  >
                    <Input size="large" />
                  </Form.Item>

                  <Form.Item
                    className="addRecipientHalfField"
                    name="bankAccountNumber"
                    label="Account Number"
                    rules={[
                      {
                        required: true,
                        message: "Please enter account number",
                      },
                    ]}
                  >
                    <Input size="large" inputMode="numeric" />
                  </Form.Item>
                </div>
              </div>
            ) : null}

            <Form.Item name="recipientCountry" hidden>
              <Input />
            </Form.Item>
            <Form.Item
              name="recipientDeliveryMethod"
              hidden
              rules={[{ required: true, message: "Please select delivery method" }]}
            >
              <Input />
            </Form.Item>

            <Form.Item className="addRecipientActions">
              <Button
                className="signupbtn"
                type="primary"
                htmlType="submit"
                style={{ width: "100%" }}
              >
                Add
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </Content>
  );
};

export default AddRecipient;
